import React, { useState } from 'react'
import { useForm, SubmitHandler } from 'react-hook-form'
import { CurrencyFormatter } from 'Helpers'
import authService from 'Services/authService'
import { ShowTGAlert } from 'Helpers/ShowTGAlert'

type FormInputs = {
  note: string
  amount: number
}

const TransferFunds: React.FC<{
  setTitle: (value: string) => void
  toggle: (completeStatus: boolean) => void
  idempotencyKey: string | undefined
}> = ({ setTitle, toggle, idempotencyKey }) => {
  const [formData, setFormData] = useState<FormInputs>()
  const [showPreview, setShowPreview] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isCompleted, setIsCompleted] = useState(false)
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInputs>()

  const { showAlert } = ShowTGAlert()

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    setFormData(data)
    setShowPreview(true)
    setTitle('Payout Summary')
  }

  const completePayout = async () => {
    setIsSubmitting(true)
    await authService
      .paystackTransfer({
        idempotencyKey,
        merchantNote: formData?.note,
        amountMajor: formData?.amount,
      })
      .then(
        () => {
          setIsCompleted(true)
          setIsSubmitting(false)
          setShowPreview(false)
          toggle(true)
        },
        (error) => {
          console.log({ error })
          toggle(false)
          showAlert({
            message: error.response.data.error,
            show: true,
            title: 'Error',
            type: 'error',
          })
          setIsSubmitting(false)
        },
      )
  }

  return (
    <div className='transfer-funds'>
      {showPreview === false && isCompleted === false && (
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className='form-input mb-3'>
            <label htmlFor='amount'>Amount</label>
            <input
              id='amount'
              type='number'
              className='form-control'
              step='0.01'
              {...register('amount', {
                required: 'Amount is required',
                valueAsNumber: true,
                min: { value: 0.01, message: 'Amount must be greater than zero' },
              })}
            />
            {errors.amount && <span className='err text-danger'>{errors.amount.message}</span>}
          </div>

          <div className='form-input mb-3'>
            <label htmlFor='note'>Note</label>
            <textarea
              id='note'
              className='form-control'
              rows={6}
              {...register('note', { required: 'Note is required' })}
            ></textarea>
            {errors.note && <span className='err text-danger'>{errors.note.message}</span>}
          </div>

          <button type='submit' className='btn btn-default mt-3 w-100' disabled={isSubmitting}>
            Transfer
          </button>
        </form>
      )}

      {showPreview === true && isCompleted === false && (
        <div className='summary'>
          <div className='row mb-3'>
            <div className='col-6 col-xl-8 label'>Payout Amount request</div>
            <div className='col-6 col-xl-4 text-end'>
              <div className='title'> N{CurrencyFormatter(formData?.amount ?? 0)}</div>
            </div>

            <div className='col-3 col-xl-2 label'>Note</div>
            <div className='col-9 col-xl-10 text-end'>
              <div className='title'>{formData?.note}</div>
            </div>
          </div>

          <button
            type='submit'
            className='btn btn-default btn-transfer mt-5 w-100'
            // disabled={isSubmitting}
            onClick={() => isSubmitting === false && completePayout()}
          >
            {isSubmitting ? (
              <span className='spinner-border spinner-border-sm'></span>
            ) : (
              'Complete Payout Request'
            )}
          </button>
        </div>
      )}

      {isCompleted && (
        <div className='completed text-center'>
          <div className='icon mb-3'>
            <svg
              width='64'
              height='63'
              viewBox='0 0 64 63'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <circle cx='32.0005' cy='31.4438' r='31.4438' fill='#38C172' />
              <path
                d='M21.1953 32.8938L27.1762 38.6988L43.1254 23.2188'
                stroke='white'
                strokeWidth='4.23077'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
          <div className='title'>Payout Complete!</div>
          <div className='details'>
            ₦{CurrencyFormatter(formData?.amount ?? 0)} has been successfully transferred to your
            settlement account.
          </div>
          <div className='details mt-3'>You will receive a confirmation shortly..</div>

          <button
            type='submit'
            className='btn btn-default btn-transfer mt-4 w-25'
            onClick={() => toggle(false)}
          >
            Done
          </button>
        </div>
      )}
    </div>
  )
}

export default TransferFunds
